<script>
  import { onMount, beforeUpdate } from "svelte";
  import { slide } from "svelte/transition";

  /* Services */
  import { cart } from "../../models/cart.js";
  import { stripe } from "../../services/stripe.js";
  import { auth } from "../../services/auth.js";
  import { scrollToY, formatInt, formatPrice } from "../../services/utilities.js";
  import { track } from "../../services/analytics.js";
  import { pendingPlan } from "../../models/pendingPlan.js";
  import { subscription } from "../../models/subscription.js";
  import {
    getShippingRates,
    getSessionID,
    validateAddress,
    getTaxes,
  } from "../../models/orders.js";
  import { getAccountBalance } from "../../models/giftCard.js";

  /* Components */
  import CartList from "../../components/CartList.svelte";
  import AddressForm from "../../components/AddressForm.svelte";
  import Discounts from "../../components/Discounts.svelte";
  import OrderTotals from "../../components/OrderTotals.svelte";
  import Popup from "../../components/Popup.svelte";
  import ShippingRates from "../../components/ShippingRates.svelte";
  import Alert from "../../components/Alert.svelte";
  import GiftCardLookup from "../../components/GiftCardLookup.svelte";

  let errors = [];
  let errorMsg;
  let clearError = () => (errorMsg = null);
  let user = null;
  let giftBalance = 0;
  let data = {};
  let isDataInit = false;
  let taxesLoadedSubtotal = 0;

  let pendingSubmit = false;

  let shipRequired = true;
  var active = 0;
  var steps = [
    { name: "Cart", opened: true, complete: true, validate: checkCart },
    {
      name: "Shipping",
      opened: false,
      complete: false,
      validate: checkShipping,
    },
    { name: "Discounts", opened: false, complete: false, validate: () => true },
    {
      name: "Gift Card",
      opened: false,
      complete: false,
      validate: () => true,
    },
    {
      name: "Additional Options",
      opened: false,
      complete: false,
      validate: checkOptions,
    },
  ];

  const giftCardStep = steps.findIndex((s) => s.name === "Gift Card");

  // variable for questions to ask the customer based on their order
  $: userQuestions = [];
  $: hasTicket = $cart.some((p) => p.product_type.name === "Ticket");
  $: hasMultipleTickets =
    $cart.filter((p) => p.product_type.name === "Ticket").length > 1 ||
    $cart.find((p) => p.product_type.name === "Ticket")?.quantity > 1;

  async function changeStep(dir) {
    var isValid = true;
    errors = [];

    if (!shipRequired && steps[active].name === "Cart") {
      // if shipping isn't required and its on the cart tab, add one more
      dir = dir + 1;
    }
    if (!shipRequired && steps[active].name === "Discounts" && dir < 0) {
      // if shipping isn't required and its on the discounts tab, subtract one more
      dir = dir - 1;
    }

    if (dir > 0) {
      isValid = await steps[active].validate();
      errors = errors;

      if (isValid) {
        steps[active].complete = true;
        active = active + dir;
        steps[active].opened = true;
        steps[active].complete = false;
      }
    } else {
      active = active + dir;
    }
  }

  function loadAddress(attrs) {
    var userAddress = attrs.find((a) => a.name === "address");
    if (userAddress) {
      data.details.shipping = JSON.parse(userAddress.value);
    }
  }

  function checkIfShippingNeeded() {
    return $cart.some(
      (x) =>
        x.product_type.shippable === true || x.product_type.taxable === true
    );
  }

  beforeUpdate(async () => {
    // fetch taxes on gift card tab loaded
    if (active === giftCardStep && taxesLoadedSubtotal !== calculateTotal()) {
      // We no longer fetch taxes and fees prior to checkout due to changes in the backend
      let taxesAndFees = await getTaxes($cart, data);
      data.taxes = taxesAndFees.taxes;
      data.fees = taxesAndFees.fees;
      data.tax_calculation_id = taxesAndFees.tax_calculation_id;
      taxesLoadedSubtotal = calculateTotal();
    }
  });

  onMount(async () => {
    // if there is a session id in local storage, the user most likely pressed back from the checkout session page
    // lets redirect them to the cart
    let existingSesh = stripe.getSessionIDStored();
    if (existingSesh !== "" && existingSesh !== null) {
      window.location.href = "/";
    }
    user = auth.getAuthenticatedUser();
    if (user) {
      // remind customers with a gift balance where to use it
      // the reminder is optional, so a failed request just means no banner
      getAccountBalance(false)
        .then((res) => {
          if (!res.error) giftBalance = res.balance;
        })
        .catch(() => {});
    }

    // load cart and initialize data object
    await cart.loadCart();
    if (user) addPendingPlan();
    initializeData();

    // skip when we're redirecting away from a stale stripe session or the cart is empty
    if (!existingSesh && $cart.length > 0) {
      let plan = $cart.find((c) => c.product_type.name === "Subscription");
      track("begin_checkout", {
        currency: "USD",
        value: data.cart.total / 100,
        has_subscription: !!plan,
        plan_name: plan ? plan.title : undefined,
      });
    }

    shipRequired = checkIfShippingNeeded();
    if (!shipRequired) {
      steps[1].name = "Shipping - Not Needed";
    }

    // if user exists, lets see if theres an address
    if (user) {
      auth.getUserDetails(loadAddress);
    }

    if (hasTicket) {
      userQuestions = [
        ...userQuestions,
        {
          question: "Enter your team's name.",
          answer: "",
          removable: false,
        },
        {
          question:
            "Already have your team ready? Tell us who's joining you! Please include each teammate's full name and email.",
          answer: "",
          removable: false,
        },
      ];
    }
  });

  // a plan picked before signing up is saved by SubscriptionOptions; now that
  // the user is signed in and their subscription status is loaded, add it.
  // Mirrors the checks in SubscriptionOptions.addSubToCart.
  function addPendingPlan() {
    let plan = pendingPlan.get();
    if (!plan) return;
    pendingPlan.clear();

    if ($subscription.hasSubscription) {
      errors = [
        "You already have an active subscription plan. View your subscription details <a href='/account'>here</a>.",
      ];
    } else if (cart.hasMembershipInCart()) {
      // same plan is already there, nothing to do; a different one needs the
      // customer to choose, so don't silently drop the plan they just picked
      if (!$cart.some((c) => c.sku === plan.product.sku)) {
        errors = [
          "You already have a subscription plan in your cart. Please remove it first, if you would like to change the plan.",
        ];
      }
    } else if (cart.hasPuzzlePack()) {
      errors = [
        "Please remove the puzzle pack and rentals from your cart, if you would like to sign up for a subscription plan.",
      ];
    } else {
      cart.add(plan.product);
    }
  }

  // allow customer to
  function removeQuestion(idx) {
    userQuestions = userQuestions.filter((q, i) => i !== idx);
  }

  // add another team to get more questions
  function addAnotherTeam() {
    userQuestions = [
      ...userQuestions,
      {
        question: "Enter your team's name.",
        answer: "",
        removable: false,
      },
      {
        question:
          "Already have your team ready? Tell us who's joining you! Please include each teammate's full name and email.",
        answer: "",
        removable: false,
      },
    ];
  }

  function initializeData() {
    data = {
      cart: {
        quantity: $cart.reduce((acc, curr) => acc + Number(curr.quantity), 0),
        total: $cart.reduce(
          (acc, curr) =>
            acc +
            (Number(curr.sale_price)
              ? Number(curr.sale_price)
              : Number(curr.price)) *
              curr.quantity,
          0
        ),
      },
      selected_ship_rate: {
        shipment_charge_total_int: 0.0,
        courier_id: null,
      },
      discounts: {
        coupon_code: "",
        total: 0.0,
      },
      gift_card_id: null,
      gift_card_valid: false,
      gift_card_amount: 0,
      gift_card_balance: 0,
      apply_balance: false,
      apply_balance_amount: 0,
      rates: [],
      details: {
        comments: "",
        shipping: {},
      },
      email: "", // For when user is checking out as guest
      options: {
        terms: false,
        newsletter: true,
      },
      taxes: 0,
      fees: [],
      tax_calculation_id: "",
    };
    isDataInit = true;
  }

  function calculateTotal() {
    let feeTotal = data.fees.reduce((acc, obj) => {
      return acc + obj.price;
    }, 0);
    return parseInt(
      data.cart.total +
        data.selected_ship_rate.shipment_charge_total_int -
        data.discounts.total +
        feeTotal +
        data.taxes -
        data.gift_card_amount -
        data.apply_balance_amount
    );
  }

  function checkCart() {
    var hasRental = $cart.some((f) => f.title == "Puzzle Pack");
    if (!user && hasRental) {
      errors.push(
        'You must <a href="/login?referrer=/checkout">sign in</a> or <a href="/register">create an account</a> to order a puzzle pack!'
      );
      return false;
    }

    return true;
  }

  /* Start SHIPPING */
  async function checkShipping() {
    errors = [];
    await checkAddress(false);

    // If any address errors, return
    if (errors.length > 0) return false;

    // Address is good, check for a shipping rate
    if (!data.selected_ship_rate.courier_id) {
      errors.push("Please select a shipping rate!");
      return false;
    }

    return true;
  }

  let shippingAddressConfirm = null;
  let shippingAddressUpdate = null;
  async function checkAddress(getRates) {
    // Check that the address is not empty
    if (
      !data.details.shipping ||
      !data.details.shipping.address ||
      !data.details.shipping.first ||
      !data.details.shipping.last ||
      !data.details.shipping.state ||
      !data.details.shipping.city ||
      !data.details.shipping.zip
    ) {
      errors.push("Please fill out the shipping address!");
    }

    // Check for a phone number
    if (!data.details.shipping.phone) {
      errors.push("Please fill in a phone number!");
    }

    if (getRates) {
      let addressResult = await validateAddress(data.details.shipping);
      if (addressResult.error) {
        shippingAddressConfirm = `
          ${data.details.shipping.address} ${
            data.details.shipping.address2 || ""
          }
          ${data.details.shipping.city}, ${data.details.shipping.state} ${
            data.details.shipping.zip
          }`;
      } else {
        // if the address matches from the backend, don't show popup
        if (!addressResult.valid) {
          shippingAddressConfirm = addressResult.address;
          shippingAddressUpdate = addressResult.addressStruct;
        } else {
          // else address is good, lets get shipping rates + taxes
          data.details.shipping = addressResult.addressStruct;
          await getShipping();
        }
      }
    }
  }
  /* End SHIPPING */

  function checkOptions() {
    if (!user) {
      if (!document.getElementById("guest-email").checkValidity()) {
        errors.push("Please add a valid email address.");
      }
    }

    if (userQuestions) {
      for (let i in userQuestions) {
        if (userQuestions[i].answer === "") {
          errors.push("Please answer all the questions provided.");
        }
      }
    }

    if (!data.options.terms) {
      errors.push("Please accept our terms and conditions.");
    }

    if (errors.length) {
      return false;
    }

    return true;
  }

  async function submit() {
    errors = [];

    if (!checkOptions()) {
      errors = errors;
      return;
    }

    try {
      /* Remove for now, might add in the future
      if(user){
        auth.updateUserDetails(
          "custom:emailCommunication",
          data.options.newsletter.toString()
        );
      }*/

      // store session id to localstorage if not subscription rental
      pendingSubmit = true;
      if (userQuestions.length) {
        data.details.comments += `\n\n--- User's Answers Below ---`;
        for (let i in userQuestions) {
          data.details.comments += `\n ${userQuestions[i].question} : ${userQuestions[i].answer}`;
        }
      }
      let session = await getSessionID($cart, data);

      if (session.sessionID === undefined)
        throw "We are experiencing issues, please try again in a few minutes";

      if (!session.redirectToCheckout) {
        // don't redirect to stripe
        cart.empty();
        window.location.href = "/success";
      } else {
        stripe.storeSession(session.sessionID);
        try {
          await stripe.redirect(session.sessionID);
        } catch (err) {
          errorMsg = err;
        }
      }
    } catch (err) {
      if (err.message) {
        errorMsg = err.message;
      } else {
        errorMsg = err;
      }
    }
    pendingSubmit = false;
  }

  let fetchingRates = false;
  async function getShipping() {
    if (!fetchingRates) {
      fetchingRates = true;
      data.rates = await getShippingRates(data, $cart);
      if (data.rates) {
        data.selected_ship_rate = data.rates[0];
      } else {
        data.rates = [];
        errors = ["Oh no! Unable to load shipping rates!"];
      }

      fetchingRates = false;
    }
  }

  function addressChange() {
    data.selected_ship_rate.courier_id = null;
    data.selected_ship_rate.shipment_charge_total_int = 0.0;
    data.rates = [];
    data.taxes = 0;

    if (data.discounts.free_shipping) {
      data.discounts.coupon_code = "";
      data.discounts.total = 0.0;
    }
  }

  // need to collect cc on certain product types
  function collectCC() {
    return $cart.some(
      (c) => c.product_type.id === 4 || c.product_type.id === 5
    );
  }
</script>

{#if errorMsg}
  <Popup message={errorMsg} close={() => clearError()} />
{/if}
{#if shippingAddressConfirm}
  <Popup
    close={() => (shippingAddressConfirm = null)}
    confirm={() => {
      if (shippingAddressUpdate) {
        data.details.shipping = shippingAddressUpdate;
      }
      shippingAddressConfirm = null;
      getShipping();
    }}
    confirmText="Yes!"
    cancel={() => {
      getShipping();
      shippingAddressConfirm = null;
    }}
    cancelText={shippingAddressUpdate ? "No, keep what I have." : "No"}
    message={`Is this shipping address correct? <br/>${shippingAddressConfirm}`}
  />
{/if}

<div class="checkout">
  <header>
    <h1>Checkout</h1>

    <div class="checkout-steps btn-group">
      {#each steps as s, i}
        <button
          class="btn"
          class:btn-outline={active !== i && !s.complete}
          class:btn-success={active !== i && s.complete}
          class:btn-danger={active !== i && !s.complete && s.opened}
        >
          {#if active !== i && s.complete}
            <svg class="icon">
              <use href="/icons.svg#check" />
            </svg>
          {:else if active !== i && s.opened}
            <svg class="icon">
              <use href="/icons.svg#clear" />
            </svg>
          {/if}
          {s.name}
        </button>
      {/each}
    </div>
  </header>

  <div class="checkout-content">
    {#if giftBalance > 0 && !data.apply_balance && active !== giftCardStep}
      <div class="gift-reminder" role="status">
        <svg class="icon"><use href="/icons.svg#gift" /></svg>
        <span>
          You have <b>{formatPrice(giftBalance)}</b> in gift balance. You can
          use it on the <b>Gift Card</b> step.
        </span>
      </div>
    {/if}

    {#if errors.length > 0}
      <Alert type="danger" icon="warning">
        <ul>
          {#each errors as e}
            <li>
              {@html e}
            </li>
          {/each}
        </ul>
      </Alert>
    {/if}

    <!-- Cart -->
    {#if active == 0}
      <section class="checkout-section card" transition:slide|local>
        <h2>Cart</h2>
        <CartList editable={false} />
      </section>

      <a class="text-sm" href="/cart">
        <svg class="icon">
          <use href="/icons.svg#keyboard_arrow_left" />
        </svg>
        Edit Cart
      </a>
    {/if}

    <!-- Shipping -->
    {#if active == 1}
      <section class="checkout-section card" transition:slide|local>
        <h2>Shipping</h2>

        <AddressForm
          onChange={addressChange}
          bind:address={data.details.shipping}
        />

        <h3>Rates</h3>
        {#if fetchingRates}
          <svg class="icon spin">
            <use href="/icons.svg#circle_notch" />
          </svg>
          Loading Shipping Rates...
        {:else if !data.rates.length}
          <button
            class="btn"
            on:click|preventDefault={() => checkAddress(true)}
          >
            Validate Address
          </button>
        {/if}

        {#if data.rates}
          <ShippingRates
            rates={data.rates}
            bind:selectedRate={data.selected_ship_rate}
          />
        {/if}
      </section>
    {/if}

    <!-- Discounts -->
    {#if active == 2}
      <section class="checkout-section card" transition:slide|local>
        <h2>Discounts</h2>
        <Discounts products={$cart} bind:details={data} />
      </section>
    {/if}

    {#if active == giftCardStep}
      <section class="checkout-section card" transition:slide|local>
        <h2>Gift Cards</h2>
        <GiftCardLookup
          {user}
          refer="checkout"
          checkout={true}
          bind:valid={data.gift_card_valid}
          bind:balance={data.gift_card_amount}
          bind:giftCardId={data.gift_card_id}
          bind:applyAccountBalanceAmount={data.apply_balance_amount}
          bind:isUserApplyingBalance={data.apply_balance}
          checkoutTotal={calculateTotal}
        />
      </section>
    {/if}

    <!-- Additional Options -->
    {#if active == 4}
      <section class="checkout-section card" transition:slide|local>
        <h2>Additional Options</h2>

        {#if !user}
          <div class="form-field">
            <label class="form-field-label" for="guest-email">
              Email Address
            </label>
            <input
              class="form-input"
              type="email"
              id="guest-email"
              bind:value={data.email}
              required
            />
          </div>
        {/if}

        {#if userQuestions.length}
          <h3>Lets gather some information for the upcoming event!</h3>

          <div class="user-questions">
            {#each userQuestions as quest, idx}
              <div class="user-question">
                <div class="form-field">
                  <label for="quest-{idx}" class="form-field-label"
                    >{quest.question}</label
                  >
                  <input
                    id="quest-{idx}"
                    class="form-input"
                    bind:value={quest.answer}
                  />
                </div>
                {#if quest.removable}
                  <div>
                    <button
                      class="btn btn-link"
                      on:click={() => removeQuestion(idx)}>X</button
                    >
                  </div>
                {/if}
              </div>
            {/each}
            <!--TODO: Only show this any of the ticket quantity > 1 -->
            {#if hasMultipleTickets}
              <button class="btn" on:click={() => addAnotherTeam()}
                >Add another team</button
              >
            {/if}
          </div>
        {/if}

        <div class="form-field">
          <label class="form-field-label">Order Notes (Optional)</label>
          <textarea class="form-input" bind:value={data.details.comments} />
        </div>

        <div class="form-field">
          <label>
            <input type="checkbox" bind:checked={data.options.terms} required />
            I agree to the
            <a href="/info/terms-and-conditions" target="_blank">
              terms and conditions
            </a>
          </label>
        </div>

        {#if collectCC() && calculateTotal() === 0}
          <Alert type="warning" icon="warning">
            Credit card is required for memberships and puzzle packs. Please
            click "Proceed" to setup future payments. You will not be charged at
            this time.
          </Alert>
        {/if}

        <!-- Remove for now, might add in future
      <div class="form-field">
        <label>
          <input type="checkbox" bind:checked={data.options.newsletter} />
          Subscribe to our newsletter!
        </label>
      </div>
      -->
      </section>
    {/if}
  </div>

  <div class="checkout-controls">
    <section class="checkout-section card">
      {#if isDataInit}
        <OrderTotals
          subtotal={data.cart.total}
          shipping={data.selected_ship_rate.shipment_charge_total_int}
          discounts={data.discounts}
          giftCard={{
            id: data.gift_card_id,
            amount: data.gift_card_amount,
            valid: data.gift_card_valid,
          }}
          applyAccountBalanceAmount={data.apply_balance_amount}
          fees={data.fees || []}
          taxes={data.taxes}
        />
      {/if}
    </section>

    <!-- Form actions -->
    <section class="checkout-section card">
      <div class="btn-group btn-group-expanded">
        <button
          class="btn"
          disabled={active === 0}
          on:click={() => changeStep(-1)}
        >
          Back
        </button>

        {#if active === 4}
          {#if calculateTotal() === 0}
            <button class="btn" disabled={pendingSubmit} on:click={submit}>
              {#if pendingSubmit}
                <svg class="icon spin">
                  <use href="/icons.svg#circle_notch" />
                </svg>
                Adding
              {:else if collectCC()}Proceed{:else}Submit Order{/if}
            </button>
          {:else}
            <button class="btn" disabled={pendingSubmit} on:click={submit}>
              {#if pendingSubmit}
                <svg class="icon spin">
                  <use href="/icons.svg#circle_notch" />
                </svg>
                Adding
              {:else}
                <svg class="icon">
                  <use href="/icons.svg#cc-stripe" />
                </svg>
                Pay Now
              {/if}
            </button>
          {/if}
        {:else}
          <button
            class="btn"
            on:click={() => {
              changeStep(1);
              scrollToY(0);
            }}
          >
            Next
          </button>
        {/if}
      </div>
    </section>

    <div class="text-sm">
      <a href="/rent" rel="external">
        <svg class="icon">
          <use href="/icons.svg#keyboard_arrow_left" />
        </svg>
        Continue Shopping
      </a>
    </div>
  </div>
</div>

<style type="text/less">
  .gift-reminder {
    display: flex;
    align-items: center;
    gap: var(--spacing-sm);
    background: var(--col-success-xl);
    border: 2px solid var(--col-success-d);
    border-radius: 8px;
    padding: var(--spacing-sm) var(--spacing);
    margin-bottom: var(--spacing);
    font-size: 1.125rem;

    .icon {
      flex: 0 0 auto;
      width: 1.75rem;
      height: 1.75rem;
    }
  }

  .checkout {
    max-width: var(--content-width);
    margin: auto;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    padding: var(--spacing-sm);

    header {
      width: 100%;
    }
  }

  .checkout-content {
    flex: 0 1 69%;
  }

  .checkout-controls {
    flex: 0 1 30%;
  }

  .checkout-steps {
    width: 100%;
    margin: var(--spacing-sm) 0;
  }

  .checkout-section {
    margin: var(--spacing-sm) 0;
  }

  @media (max-width: 650px) {
    .checkout {
      display: block;
    }
  }

  @media (min-width: 800px) {
    .checkout {
      padding: var(--spacing);
    }
  }

  @media (max-width: 750px) {
    .checkout-steps {
      margin: var(--spacing-sm) 0;
      display: flex;
      flex-direction: column;
    }
  }

  .user-question {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    .form-field {
      width: 95%;
    }
  }
</style>
